import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, PlugZap, Trash2, Save, UploadCloud, Rocket, Palette, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, fmtRel } from "@/lib/api";
import { useMe } from "@/lib/me";
import { stageLabel, can } from "@/lib/constants";
import { PageHeader, Pill, WebflowBadge, Dot, EmptyState, StageTag } from "@/components/app/shared";
import GoogleWorkspaceCard from "@/components/google/GoogleWorkspaceCard";
import CanvaCard from "@/components/google/CanvaCard";
import CrmDriveCard from "@/components/google/CrmDriveCard";

const PROVIDERS = [
  { id: "gemini", label: "Google Gemini", hint: "Clave de Google AI Studio (AIza…)" },
  { id: "openai", label: "OpenAI", hint: "Clave de OpenAI (sk-…)" },
  { id: "anthropic", label: "Anthropic Claude", hint: "Clave de Anthropic (sk-ant-…)" },
];
const TYPE_LABEL = { PlainText: "Texto", RichText: "Texto enriquecido", Image: "Imagen", MultiImage: "Multi-imagen", DateTime: "Fecha/Hora", Reference: "Referencia", MultiReference: "Multi-referencia", Link: "Enlace", Switch: "Interruptor" };
const STATUS = {
  ok: { tone: "mint", label: "Verificado" },
  type_mismatch: { tone: "butter", label: "Tipo distinto" },
  missing: { tone: "blush", label: "No encontrado" },
  unverified: { tone: "neutral", label: "Sin verificar" },
};

function SecretInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex gap-1.5">
      <Input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="font-mono text-xs" autoComplete="off" data-testid={testid} />
      <Button type="button" variant="ghost" size="icon" onClick={() => setShow(!show)} aria-label={show ? "Ocultar" : "Mostrar"}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
    </div>
  );
}

function AISettings({ s }) {
  const qc = useQueryClient();
  const [provider, setProvider] = useState(s.provider);
  const [model, setModel] = useState(s.model);
  const [key, setKey] = useState("");
  useEffect(() => { setProvider(s.provider); setModel(s.model); }, [s.provider, s.model]);
  const inv = () => { qc.invalidateQueries({ queryKey: ["integrations"] }); qc.invalidateQueries({ queryKey: ["settings-public"] }); };
  const save = useMutation({
    mutationFn: async () => (await api.put("/settings/ai", { provider, model, apiKey: key || null })).data,
    onSuccess: () => { setKey(""); inv(); toast.success("Configuración de IA guardada (clave cifrada)"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const test = useMutation({ mutationFn: async () => (await api.post("/settings/ai/test")).data, onSuccess: () => toast.success("Conexión con la IA correcta"), onError: (e) => toast.error(errMsg(e)) });
  const remove = useMutation({ mutationFn: async () => api.delete("/settings/ai/key"), onSuccess: () => { inv(); toast.success("Clave eliminada"); } });
  const models = s.modelOptions[provider] || [];
  return (
    <section className="rounded-lg border border-border bg-card" data-testid="ai-settings-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold"><KeyRound className="h-4 w-4" /> Motor de IA</div>
          <div className="text-xs text-muted-foreground">Usado por el Radar y el Social Pack. Todo el contenido se genera en español.</div>
        </div>
        <Pill data-testid="ai-key-status"><Dot tone={s.keySet ? "ok" : "bad"} />{s.keySet ? `Clave configurada ${s.keyMasked}` : "Sin clave"}</Pill>
      </div>
      <div className="grid gap-4 p-5 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label className="text-xs">Proveedor</Label>
          <Select value={provider} onValueChange={(v) => { setProvider(v); setModel(s.modelOptions[v][0]); }}>
            <SelectTrigger data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Modelo</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from(new Set([model, ...models])).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5 md:col-span-2">
          <Label className="text-xs">{s.keySet ? "Reemplazar clave de API" : "Clave de API"}</Label>
          <SecretInput value={key} onChange={setKey} placeholder={PROVIDERS.find((p) => p.id === provider)?.hint} testid="ai-key-input" />
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Se guarda cifrada en la base de datos y nunca se muestra completa. {s.updatedAt && `Actualizada ${fmtRel(s.updatedAt)}.`}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-border px-5 py-3">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="ai-save-button"><Save className="mr-1.5 h-4 w-4" /> Guardar</Button>
        <Button variant="outline" onClick={() => test.mutate()} disabled={!s.keySet || test.isPending} data-testid="ai-test-button">
          {test.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlugZap className="mr-1.5 h-4 w-4" />} Probar conexión
        </Button>
        {s.keySet && <Button variant="ghost" onClick={() => remove.mutate()} data-testid="ai-remove-key-button"><Trash2 className="mr-1.5 h-4 w-4" /> Eliminar clave</Button>}
      </div>
    </section>
  );
}

function WebflowSettings({ s }) {
  const qc = useQueryClient();
  const [token, setToken] = useState("");
  const [siteId, setSiteId] = useState(s.siteId || "");
  const [collectionId, setCollectionId] = useState(s.collectionId || "");
  const [fieldMap, setFieldMap] = useState(s.fieldMap);
  const [info, setInfo] = useState(null);
  useEffect(() => { setSiteId(s.siteId || ""); setCollectionId(s.collectionId || ""); setFieldMap(s.fieldMap); }, [s.siteId, s.collectionId, s.fieldMap]);
  const inv = () => { qc.invalidateQueries({ queryKey: ["integrations"] }); qc.invalidateQueries({ queryKey: ["settings-public"] }); };
  const save = useMutation({
    mutationFn: async () => (await api.put("/settings/webflow", { token: token || null, siteId, collectionId, fieldMap })).data,
    onSuccess: () => { setToken(""); inv(); toast.success("Integración con Webflow guardada"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const test = useMutation({
    mutationFn: async () => (await api.post("/settings/webflow/test")).data,
    onSuccess: (r) => {
      setInfo(r); inv(); qc.invalidateQueries({ queryKey: ["webflow-schema"] }); qc.invalidateQueries({ queryKey: ["webflow-refs"] });
      const rows = r.schema?.rows || [];
      const bad = rows.filter((x) => x.status !== "ok").length;
      if (bad) toast.warning(`Conectado · ${rows.length - bad}/${rows.length} campos verificados`); else toast.success(`Conexión correcta · ${rows.length}/${rows.length} campos verificados`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const schema = s.schema || { rows: [], unmapped: [] };
  return (
    <section className="rounded-lg border border-border bg-card" data-testid="webflow-settings-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <div className="text-sm font-semibold">Webflow CMS · API v2</div>
          <div className="text-xs text-muted-foreground">Token de sitio con permisos CMS lectura/escritura. Site ID y Collection ID desde los ajustes del sitio.</div>
        </div>
        <Pill data-testid="webflow-token-status"><Dot tone={s.tokenSet && s.collectionId ? "ok" : "bad"} />{s.tokenSet ? `Token ${s.tokenMasked}` : "Sin token"}</Pill>
      </div>
      <div className="grid gap-4 p-5 md:grid-cols-3">
        <div className="grid gap-1.5 md:col-span-3">
          <Label className="text-xs">{s.tokenSet ? "Reemplazar API Token" : "API Token"}</Label>
          <SecretInput value={token} onChange={setToken} placeholder="Token de la API de Webflow" testid="webflow-token-input" />
        </div>
        <div className="grid gap-1.5"><Label className="text-xs">Site ID</Label><Input value={siteId} onChange={(e) => setSiteId(e.target.value)} className="font-mono text-xs" data-testid="webflow-site-input" /></div>
        <div className="grid gap-1.5 md:col-span-2"><Label className="text-xs">Collection ID (Artículos)</Label><Input value={collectionId} onChange={(e) => setCollectionId(e.target.value)} className="font-mono text-xs" data-testid="webflow-collection-input" /></div>
      </div>
      <div className="border-t border-border px-5 py-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="kicker">Mapeo 1:1 · colección «Artículos» ({schema.rows.length} campos)</div>
          <Pill tone={schema.verified ? "mint" : "neutral"} data-testid="webflow-map-verified">{schema.verified ? `Verificado ${schema.checkedAt ? fmtRel(schema.checkedAt) : ""}` : "Sin verificar: prueba la conexión"}</Pill>
        </div>
        <div className="overflow-hidden rounded-md border border-border">
          <Table data-testid="webflow-field-map-table">
            <TableHeader>
              <TableRow>{["Campo en Webflow", "Tipo", "Slug", "Estado"].map((h) => <TableHead key={h} className="h-9 text-[11px] uppercase tracking-wider">{h}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {schema.rows.map((r) => (
                <TableRow key={r.key} data-testid={`webflow-map-row-${r.key}`}>
                  <TableCell className="py-1.5 text-[12.5px]">{r.label}{r.required && <span className="ml-0.5 text-bad">*</span>}</TableCell>
                  <TableCell className="py-1.5 text-[11px] text-muted-foreground">
                    {TYPE_LABEL[r.expectedType] || r.expectedType}
                    {r.status === "type_mismatch" && <div className="text-[10px]">Webflow: {TYPE_LABEL[r.webflowType] || r.webflowType}</div>}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Input value={fieldMap?.[r.key] ?? r.slug ?? ""} onChange={(e) => setFieldMap({ ...fieldMap, [r.key]: e.target.value })} className="h-7 w-[200px] font-mono text-[11px]" data-testid={`webflow-map-${r.key}`} />
                  </TableCell>
                  <TableCell className="py-1.5"><Pill tone={STATUS[r.status]?.tone || "neutral"}>{STATUS[r.status]?.label || r.status}</Pill></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {schema.unmapped?.length > 0 && (
          <div className="mt-3 rounded-md border border-butter-strong bg-butter px-3 py-2 text-[11.5px] text-ink" data-testid="webflow-unmapped-fields">
            Campos de Webflow que el Hub no envía: {schema.unmapped.map((u) => `${u.label} (${u.slug})`).join(", ")}
          </div>
        )}
        <div className="mt-2 text-[11px] text-muted-foreground">Los slugs se detectan solos por el nombre visible del campo al probar la conexión. Autor y Categoría(s) se envían como referencias (IDs de las colecciones Autores y Categorias). Solo edita un slug si Webflow no lo encuentra.</div>
      </div>
      {info && (
        <div className="border-t border-border bg-surface-2 px-5 py-3 font-mono text-[11px]" data-testid="webflow-test-result">
          {info.site && <div>Sitio: {info.site}</div>}
          {info.sites && <div>Sitios disponibles: {info.sites.map((x) => `${x.name} (${x.id})`).join(", ")}</div>}
          {info.collections && <div>Colecciones: {info.collections.map((c) => `${c.name} (${c.id})`).join(", ")}</div>}
          {info.collection && <div>Colección: {info.collection}</div>}
          {info.warnings?.map((w) => <div key={w} className="mt-1 text-[11px] text-muted-foreground" data-testid="webflow-test-warning">Aviso: {w}</div>)}
        </div>
      )}
      <div className="flex flex-wrap gap-2 border-t border-border px-5 py-3">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="webflow-save-button"><Save className="mr-1.5 h-4 w-4" /> Guardar</Button>
        <Button variant="outline" onClick={() => test.mutate()} disabled={!s.tokenSet || test.isPending} data-testid="webflow-connection-test-button">
          {test.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlugZap className="mr-1.5 h-4 w-4" />} Probar conexión
        </Button>
      </div>
    </section>
  );
}

function CanvaSettings({ s }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(s.templateUrl || "");
  const save = useMutation({
    mutationFn: async () => (await api.put("/settings/canva", { templateUrl: url })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings-public"] }); toast.success("Plantillas de Canva guardadas"); },
  });
  return (
    <section className="rounded-lg border border-border bg-card p-5" data-testid="canva-settings-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><Palette className="h-4 w-4" /> Plantillas sociales en Canva</div>
      <div className="mt-1 text-xs text-muted-foreground">Enlace de la carpeta o plantilla de Mediana que abre el botón “Abrir en Canva” del Social Pack.</div>
      <div className="mt-3 flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.canva.com/folder/…" className="font-mono text-xs" data-testid="canva-template-input" />
        <Button onClick={() => save.mutate()} data-testid="canva-save-button">Guardar</Button>
      </div>
    </section>
  );
}

function WebflowQueue({ configured }) {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["webflow-queue"], queryFn: async () => (await api.get("/webflow/queue")).data });
  const [busy, setBusy] = useState(null);
  const push = useMutation({
    mutationFn: async ({ id, live }) => { setBusy(id); return (await api.post(`/stories/${id}/webflow`, { live })).data; },
    onSuccess: (s, { live }) => { qc.invalidateQueries({ queryKey: ["webflow-queue"] }); qc.invalidateQueries({ queryKey: ["stories"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success(live ? `Publicado: ${s.title}` : `Borrador en Webflow: ${s.title}`); },
    onError: (e) => toast.error(errMsg(e)),
    onSettled: () => setBusy(null),
  });
  return (
    <section className="rounded-lg border border-border bg-card" data-testid="webflow-queue">
      <div className="border-b border-border px-5 py-4">
        <div className="text-sm font-semibold">Cola de sincronización</div>
        <div className="text-xs text-muted-foreground">Historias en «Lista para Webflow» y las ya vinculadas al CMS.</div>
      </div>
      {!configured && <div className="border-b border-border bg-surface-2 px-5 py-2.5 text-xs">Webflow no está configurado: los envíos estarán deshabilitados hasta que un Superadmin agregue el token y la colección.</div>}
      {isLoading ? <div className="p-5 text-sm text-muted-foreground">Cargando…</div> : data.length === 0 ? (
        <div className="p-5"><EmptyState title="Cola vacía" text="Mueve historias a «Lista para Webflow» en la Mesa Editorial." /></div>
      ) : (
        <Table>
          <TableHeader><TableRow>{["Historia", "Etapa", "Estado Webflow", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {data.map((s) => (
              <TableRow key={s.id} data-testid="webflow-queue-row">
                <TableCell className="max-w-[380px]"><Link to={`/editorial/${s.id}`} className="block truncate text-sm font-medium hover:underline">{s.title}</Link><div className="font-mono text-[10.5px] text-muted-foreground">/{s.slug}</div></TableCell>
                <TableCell><StageTag status={s.status} label={stageLabel(s.status)} /></TableCell>
                <TableCell><WebflowBadge status={s.webflowStatus} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" disabled={!configured || busy === s.id} onClick={() => push.mutate({ id: s.id, live: false })} data-testid="queue-push-draft"><UploadCloud className="mr-1 h-3.5 w-3.5" /> Borrador</Button>
                    <Button size="sm" disabled={!configured || busy === s.id} onClick={() => push.mutate({ id: s.id, live: true })} data-testid="queue-publish">{busy === s.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Rocket className="mr-1 h-3.5 w-3.5" />} Publicar</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

export default function Integrations() {
  const me = useMe();
  const isSA = me.role === "SUPERADMIN";
  const { data: pub } = useQuery({ queryKey: ["settings-public"], queryFn: async () => (await api.get("/settings/public")).data });
  const { data: s } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/settings/integrations")).data, enabled: isSA });
  return (
    <div className="space-y-6">
      <PageHeader kicker="06 · Webflow Sync & Integraciones" title="Webflow & Integraciones" subtitle="Sincroniza historias con el CMS de Webflow y configura las claves de IA y las plantillas de Canva." />
      <GoogleWorkspaceCard isSuperadmin={isSA} />
      <div className="grid gap-6 xl:grid-cols-2">
        <CrmDriveCard canManage={can(me, "manage.integrations")} />
        <CanvaCard canManage={can(me, "manage.integrations")} />
      </div>
      <WebflowQueue configured={pub?.webflowConfigured} />
      {isSA ? (
        s && (
          <div className="grid gap-6 xl:grid-cols-2">
            <WebflowSettings s={s.webflow} />
            <div className="space-y-6">
              <AISettings s={s.ai} />
              <CanvaSettings s={s.canva} />
            </div>
          </div>
        )
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-surface-2 p-5 text-sm text-muted-foreground">Las claves de API (Webflow, IA) solo las gestiona un Superadmin.</div>
      )}
    </div>
  );
}
